import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { LFSEOfferType } from 'home/components/LookingForSomethingElse/constants'
import { LOOKING_FOR_SOMETHING_ELSE_CONTENT } from 'home/components/LookingForSomethingElse/constants'
import TextButton from 'LuxKit/Button/TextButton'
import * as Analytics from 'analytics/analytics'
import { getThemeClassName } from 'lib/theme/themeUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Typography from 'LuxKit/Typography/Typography'

const Container = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding-left: ${rem(20)};
  padding-right: ${rem(20)};
  height: 100%;
  width: ${rem(388)};

  ${mediaQueryUp.tablet} {
    padding-left: ${rem(40)};
    padding-right: ${rem(40)};
  }
`

interface Props {
  offerType: LFSEOfferType
  link?: string
}

function HeroOfferCarouselLookingForSomethingElse({ offerType, link }: Props) {
  const { to } = LOOKING_FOR_SOMETHING_ELSE_CONTENT[offerType]
  const intl = useIntl()

  const onClickViewAll = useCallback(() => {
    Analytics.trackClientEvent({
      action: 'pressed',
      subject: `topexclusive-carousel-${offerType}-viewall`,
      category: 'vertical_page',
      type: 'interaction',
    })
  }, [offerType])

  const translatedLabels = useMemo(() => ({
    hotels: intl.formatMessage({
      id: 'rp8GLoIUessbwzhlJnQ4',
      defaultMessage: 'hotels',
      description: 'Label for hotels offer type in looking for something else section',
    }),
    tours: intl.formatMessage({
      id: 'RYv-3458zFA0wVZWvU67',
      defaultMessage: 'tours',
      description: 'Label for tours offer type in looking for something else section',
    }),
    ultralux: intl.formatMessage({
      id: 'idd19VBKKRt8vOucoiOR',
      defaultMessage: 'ultra lux',
      description: 'Label for ultra lux offer type in looking for something else section',
    }),
    experiences: intl.formatMessage({
      id: '0bqtFJkceeWyrdCMphan',
      defaultMessage: 'experiences',
      description: 'Label for experiences offer type in looking for something else section',
    }),
    cruises: intl.formatMessage({
      id: 'eHyaCtedz18yGb3EG6tL',
      defaultMessage: 'cruises',
      description: 'Label for cruises offer type in looking for something else section',
    }),
    villas: intl.formatMessage({
      id: 'YfLXT1uOuIur5ZO8XyQq',
      defaultMessage: 'villas',
      description: 'Label for villas offer type in looking for something else section',
    }),
  }), [intl])

  const displayLabel = translatedLabels[offerType]

  return (
    <Container direction="vertical" horizontalAlign="center" verticalAlign="center" className={getThemeClassName({ mobile: 'inverse' })}>
      <Typography align="center">
        <VerticalSpacer gap={8}>
          <Heading align="center" variant="heading5">
            {intl.formatMessage({
              id: 'eiCh1rYGzagkLizGZw55',
              defaultMessage: 'Looking for something else?',
              description: 'Heading for looking for something else section in hero carousel',
            })}
          </Heading>
          <VerticalSpacer gap={24}>
            <BodyText colour="neutral-two" align="center" variant="medium">
              {intl.formatMessage({
                id: 'O6RC0X2g3xP-c4MHa04d',
                defaultMessage: 'Browse all destinations or {label} for more amazing offers',
                description: 'Subtext encouraging users to browse more offers on the homepage',
              }, { label: displayLabel })}
            </BodyText>
            <TextButton kind="primary" size="medium" shape="square" to={link ?? to} onClick={onClickViewAll}>
              {intl.formatMessage({
                id: 'XMHO6v1XbawuUzHKS0Pl',
                defaultMessage: 'View all',
                description: 'Button text to view all offers on the offer listing or detail page',
              })}
            </TextButton>
          </VerticalSpacer>
        </VerticalSpacer>
      </Typography>
    </Container>
  )
}

export default HeroOfferCarouselLookingForSomethingElse
